import { ChangeDetectionStrategy, Component, Injector, OnInit } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { Vision } from "@common/ADAPT.Common.Model/organisation/vision";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { OrganisationPageRouteBuilder } from "@org-common/lib/route/organisation-page-route-builder";
import { BehaviorSubject, EMPTY, forkJoin, switchMap } from "rxjs";
import { VisionService } from "../vision.service";
import { VisionAuthService } from "../vision-auth.service";

enum DisplayViewType {
    IntegratedView = "integrated-view",
    PersonView = "person-view",
    CategoryView = "category-view",
}

const TabParamKey = "tab";

@Component({
    selector: "adapt-vision-page",
    standalone: false,
    templateUrl: "./vision-page.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./vision-page.component.scss"],
})
export class VisionPageComponent extends BaseRoutedComponent implements OnInit {
    public readonly DisplayViewType = DisplayViewType;
    public visions: Vision[] = [];
    public currentTab = DisplayViewType.IntegratedView;

    public canEditTeamVision = false;
    public hasAnyCompletedIntegratedVision = false;
    public hasAnyCompletedPersonalVision = false;
    public teamId?: number;
    private triggerUpdate$ = new BehaviorSubject<void>(undefined);

    public constructor(
        injector: Injector,
        private visionService: VisionService,
        private visionAuthService: VisionAuthService,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super(injector);
        rxjsBreezeService.entityTypeChanged(Vision).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.triggerUpdate$.next());
    }

    public ngOnInit() {
        const teamId = this.getRouteParamInt("teamId");
        if (teamId) {
            this.teamId = teamId;
            this.triggerUpdate$.pipe(
                switchMap(() => forkJoin([
                    this.visionService.getVisionsByTeam(teamId),
                    this.visionAuthService.canEditVisionForTeamId(teamId),
                ])),
                this.takeUntilDestroyed(),
            ).subscribe(([visions, canEdit]) => {
                this.visions = visions;
                this.canEditTeamVision = canEdit;
                const tabFromSearchParam = this.getSearchParameterValue(TabParamKey) as DisplayViewType | null;
                if (tabFromSearchParam && Object.values(DisplayViewType).includes(tabFromSearchParam)) {
                    this.currentTab = tabFromSearchParam;
                } else {
                    this.currentTab = this.visionService.areAllVisionsCompleted(visions)
                        ? DisplayViewType.IntegratedView
                        : DisplayViewType.CategoryView;
                    this.onTabChanged(this.currentTab);
                }

                this.hasAnyCompletedIntegratedVision = this.visions.some((v) => v.extensions.isIntegrated && v.extensions.isCompleted);
                this.hasAnyCompletedPersonalVision = this.visions.some((v) => v.extensions.isPersonal && v.extensions.isCompleted);
                this.isInitialised = true;
                this.notifyActivated();
            });
        } else {
            throw new Error("No teamId route param");
        }
    }

    @Autobind
    public captureVision() {
        return this.visionService.openCaptureVisionDialog(this.teamId!).pipe(
            this.takeUntilDestroyed(),
        );
    }

    @Autobind
    public captureIntegratedVision() {
        return this.teamId
            ? this.visionService.editIntegratedVision(this.teamId, undefined, "Return to team vision page")
            : EMPTY;
    }

    @Autobind
    public assignIntegratedVision() {
        return this.teamId
            ? this.visionService.assignIntegratedVision(this.teamId)
            : EMPTY;
    }

    public onTabChanged(newTab: DisplayViewType) {
        this.setSearchParameterValue(TabParamKey, newTab);
    }
}

export const VisionPageRoute = new OrganisationPageRouteBuilder<{ teamId: number }>()
    .usingNgComponent("adapt-vision-page", VisionPageComponent)
    .atOrganisationUrl("/team/:teamId/vision")
    .verifyingFeatures(FeatureName.CompassVision)
    .withTitle("Vision")
    .build();
